import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { disablePush, enablePush, pushSupported } from "@/lib/push";
import { Button } from "@/components/ui/button";

/** Turns installed-app notifications on or off for whichever app you are in. */
export const PushToggle = ({ scope, description }) => {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/push/status").then(({ data }) => setDevices(data.devices)).catch(() => setDevices(0));
  }, []);

  const toggle = async () => {
    setBusy(true); setError("");
    try {
      if (devices > 0) {
        await disablePush();
        setDevices(0);
      } else {
        await enablePush(scope);
        const { data } = await api.get("/push/status");
        setDevices(data.devices);
      }
    } catch (e) { setError(e?.message || errMsg(e)); } finally { setBusy(false); }
  };

  const on = devices > 0;
  return (
    <div data-testid="push-toggle" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold">
        <Bell className="h-3 w-3" /> Notifications
      </p>
      <p className="mt-2 text-xs text-slate-500">
        {description || "Get an alert on your lock screen the moment something needs you."}
      </p>
      <Button data-testid="push-toggle-btn" disabled={busy || devices === null} onClick={toggle}
        className={`mt-4 ${on ? "bg-ink-700 text-slate-100 hover:bg-ink-600" : "bg-gold text-ink-900 hover:bg-gold-light"}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" />
          : on ? (<><BellOff className="mr-2 h-4 w-4" /> Turn notifications off</>)
            : (<><Bell className="mr-2 h-4 w-4" /> Turn notifications on</>)}
      </Button>
      {on && <p data-testid="push-on" className="mt-3 text-xs text-emerald-400">On for {devices} device{devices === 1 ? "" : "s"}.</p>}
      {!pushSupported() && (
        <p className="mt-3 text-xs text-slate-500">
          On iPhone: Share → Add to Home Screen, open the app from your home screen, then turn this on.
        </p>
      )}
      {error && <p data-testid="push-error" className="mt-3 text-sm text-red-400">{error}</p>}
    </div>
  );
};
